import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";

import { errorMessage } from "@/api/client";
import { useMe, useUpdateAccount } from "@/api/hooks/auth";
import type { ThemePreference } from "@/api/types";

import { oppositeTheme } from "./theme-options";
import { useTheme } from "./theme-provider";

/**
 * Applies the signed-in user's saved theme once per account, when their session
 * loads, so a preference chosen on another device follows them. Mount once (AppShell).
 */
export function useAccountThemeSync() {
  const me = useMe();
  const { setTheme } = useTheme();
  const syncedUserId = useRef<number | null>(null);

  useEffect(() => {
    if (!me || syncedUserId.current === me.id) return;
    syncedUserId.current = me.id;
    setTheme(me.theme);
  }, [me, setTheme]);
}

/**
 * Theme state for controls that change the theme (menus, toggles, settings).
 * `setTheme` applies the choice immediately and, for signed-in users, saves it to
 * their account in the background (a toast reports a failed save).
 */
export function useAccountTheme() {
  const { theme, resolvedTheme, setTheme: applyTheme } = useTheme();
  const savedTheme = useMe()?.theme;
  const { mutateAsync: updateAccount } = useUpdateAccount();

  const setTheme = useCallback(
    (next: ThemePreference) => {
      applyTheme(next);
      if (savedTheme === undefined || savedTheme === next) return;
      updateAccount({ theme: next }).catch((error: unknown) =>
        toast.error("Couldn't save your theme to your account", { description: errorMessage(error) }),
      );
    },
    [applyTheme, savedTheme, updateAccount],
  );

  const toggleTheme = useCallback(() => setTheme(oppositeTheme(resolvedTheme)), [resolvedTheme, setTheme]);

  return { theme, resolvedTheme, setTheme, toggleTheme };
}
